import {combineReducers, configureStore} from "@reduxjs/toolkit";
import {apiMiddleware} from "./middlewares/apiMiddleware";
import {TypedUseSelectorHook, useDispatch, useSelector} from "react-redux";

// Slices
import {ThemeSlice} from "./slices/ThemeSlice";
import {AuthSlice} from "./slices/AuthSlice";
import {ProfileSlice} from "./slices/ProfileSlice";
import {UserSlice} from "./slices/UserSlice";
import {PaginationSlice} from "./slices/PaginationSlice";
import {SortSlice} from "./slices/SortSlice";
import {FilterSlice} from "./slices/FilterSlice";
import {ResolutionSlice} from "./slices/ResolutionSlice";
import {SportSlice} from "./slices/SportSlice";
import {TournamentSlice} from "./slices/TournamentSlice";
import {TeamSlice} from "./slices/TeamSlice";
import {SynonymSlice} from "./slices/SynonymSlice";
import {ServerSlice} from "./slices/ServerSlice";
import {StreamSlice} from "./slices/StreamSlice";
import {StatusSlice} from "./slices/StatusSlice";
import {PlaylistSlice} from "./slices/PlaylistSlice";
import {BlackistSlice} from "./slices/BlacklistSlice";
import {CommentSlice} from "./slices/CommentSlice";
import {TakesSlice} from "./slices/TakesSlice";
import {DashboardSlice} from "./slices/DashboardSlice";

// API
import {AuthAPI} from "./api/AuthAPI";
import {ProfileAPI} from "./api/ProfileAPI";
import {UserAPI} from "./api/UserAPI";
import {SportAPI} from "./api/SportAPI";
import {TournamentAPI} from "./api/TournamentAPI";
import {TeamAPI} from "./api/TeamAPI";
import {SynonymAPI} from "./api/SynonymAPI";
import {ResolutionAPI} from "./api/ResolutionAPI";
import {ServerAPI} from "./api/ServerAPI";
import {StreamAPI} from "./api/StreamAPI";
import {StatusAPI} from "./api/StatusAPI";
import {PlaylistAPI} from "./api/PlaylistAPI";
import {CommentAPI} from "./api/CommentAPI";
import {BlacklistAPI} from "./api/BlacklistAPI";
import {TakesAPI} from "./api/TakesAPI";
import {DashboardAPI} from "./api/DashboardAPI";
import {LinkAPI} from "./api/LinkAPI";
import {LinkSlice} from "./slices/LinkSlice";

export const rootReducer = combineReducers({
	theme: ThemeSlice.reducer,
	auth: AuthSlice.reducer,
	profile: ProfileSlice.reducer,
	user: UserSlice.reducer,
	pagination: PaginationSlice.reducer,
	sort: SortSlice.reducer,
	filter: FilterSlice.reducer,
	resolution: ResolutionSlice.reducer,
	sport: SportSlice.reducer,
	tournament: TournamentSlice.reducer,
	team: TeamSlice.reducer,
	synonym: SynonymSlice.reducer,
	server: ServerSlice.reducer,
	stream: StreamSlice.reducer,
	status: StatusSlice.reducer,
	playlist: PlaylistSlice.reducer,
	blacklist: BlackistSlice.reducer,
	comment: CommentSlice.reducer,
	takes: TakesSlice.reducer,
	dashboard: DashboardSlice.reducer,
	link: LinkSlice.reducer,
});

export const API = {
	auth: AuthAPI,
	profile: ProfileAPI,
	user: UserAPI,
	sport: SportAPI,
	tournament: TournamentAPI,
	team: TeamAPI,
	synonym: SynonymAPI,
	resolution: ResolutionAPI,
	server: ServerAPI,
	stream: StreamAPI,
	status: StatusAPI,
	playlist: PlaylistAPI,
	comment: CommentAPI,
	blacklist: BlacklistAPI,
	takes: TakesAPI,
	dashboard: DashboardAPI,
	link: LinkAPI,
};

const store = configureStore({
	reducer: rootReducer,
	middleware: (getDefaultMiddleware) => getDefaultMiddleware({
		serializableCheck: false
	}).concat(apiMiddleware),
	devTools: process.env.NODE_ENV === 'development',
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export default store;

